import React from 'react';
import BrandLogo from '../../../assets/logo.png';
import type { AdminContentKind, AdminConsoleModule, AdminUser } from '../contentConsoleApi';

export type ConsoleModule = AdminConsoleModule;

export type ConsoleTopbarProps = {
  user: AdminUser;
  module: ConsoleModule;
  sidebarCollapsed: boolean;
  mobileSidebarOpen: boolean;
  contentKinds: AdminContentKind[];
  onModuleChange: (module: ConsoleModule) => void;
  onToggleCollapse: () => void;
  onToggleMobileSidebar: () => void;
  onCloseMobileSidebar: () => void;
  onLogout: () => void;
};

const Icon = ({ path }: { path: string }) => (
  <svg viewBox="0 0 20 20" aria-hidden="true">
    <path d={path} fill="currentColor" />
  </svg>
);

const ChevronLeftIcon = () => <Icon path="M12.78 4.47a.75.75 0 0 1 0 1.06L8.31 10l4.47 4.47a.75.75 0 1 1-1.06 1.06l-5-5a.75.75 0 0 1 0-1.06l5-5a.75.75 0 0 1 1.06 0Z" />;
const ChevronRightIcon = () => <Icon path="M7.22 15.53a.75.75 0 0 1 0-1.06L11.69 10 7.22 5.53a.75.75 0 1 1 1.06-1.06l5 5a.75.75 0 0 1 0 1.06l-5 5a.75.75 0 0 1-1.06 0Z" />;

const moduleMeta: Record<ConsoleModule, { title: string; description: string; icon: React.ReactNode }> = {
  news: {
    title: '新闻管理',
    description: '维护新闻内容',
    icon: <Icon path="M5 4.5h10a1 1 0 0 1 1 1v9a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-9a1 1 0 0 1 1-1Zm1 2v1.5h8V6.5H6Zm0 3v1.5h5V9.5H6Zm0 3v1.5h6v-1.5H6Z" />,
  },
  resource: {
    title: '资源管理',
    description: '维护资源内容',
    icon: <Icon path="M4.5 5.5a1 1 0 0 1 1-1h3.38a1 1 0 0 1 .7.3l.82.85a1 1 0 0 0 .72.3H14.5a1 1 0 0 1 1 1V8h-11V5.5Zm-1 4h12v5a1 1 0 0 1-1 1h-10a1 1 0 0 1-1-1v-5Z" />,
  },
  career: {
    title: '招聘管理',
    description: '维护招聘内容',
    icon: <Icon path="M7 4.5A1.5 1.5 0 0 1 8.5 3h3A1.5 1.5 0 0 1 13 4.5V5h2a1 1 0 0 1 1 1v3.5h-5v-1h-2v1H4V6a1 1 0 0 1 1-1h2v-.5Zm1.5 0V5h3v-.5h-3ZM4 10.5h5v1a1 1 0 0 0 1 1h0a1 1 0 0 0 1-1v-1h5V14a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1v-3.5Z" />,
  },
  'project-cooperation': {
    title: '项目合作',
    description: '维护合作页内容',
    icon: <Icon path="M6.7 5.2a2.2 2.2 0 1 1 3.11 3.1L8.4 9.72a2.2 2.2 0 1 1-3.11-3.11L6.7 5.2Zm6.6 5.08a2.2 2.2 0 1 1 3.11 3.11l-1.42 1.41a2.2 2.2 0 0 1-3.11-3.1l1.42-1.42ZM7.88 11.41a.75.75 0 1 1 1.06-1.06l3.18 3.18a.75.75 0 1 1-1.06 1.06l-3.18-3.18Zm3.18-6.94a.75.75 0 0 1 1.06 0l3.18 3.18a.75.75 0 1 1-1.06 1.06l-3.18-3.18a.75.75 0 0 1 0-1.06Z" />,
  },
  analytics: {
    title: '访问统计',
    description: '流量与地区分析',
    icon: <Icon path="M4 14.5h2.5V8H4v6.5Zm4.75 0h2.5V5.5h-2.5v9Zm4.75 0H16V10h-2.5v4.5ZM3.25 17A1.25 1.25 0 0 1 2 15.75v-11.5A1.25 1.25 0 0 1 3.25 3h13.5A1.25 1.25 0 0 1 18 4.25v11.5A1.25 1.25 0 0 1 16.75 17H3.25Z" />,
  },
  account: {
    title: '账号中心',
    description: '个人资料与权限配置',
    icon: <Icon path="M10 3.5a3.25 3.25 0 1 1 0 6.5 3.25 3.25 0 0 1 0-6.5ZM5.5 14a3 3 0 0 1 3-3h3a3 3 0 0 1 3 3v1.5h-9V14Z" />,
  },
};

const ConsoleTopbar: React.FC<ConsoleTopbarProps> = ({
  user,
  module,
  sidebarCollapsed,
  mobileSidebarOpen,
  contentKinds,
  onModuleChange,
  onToggleCollapse,
  onToggleMobileSidebar,
  onCloseMobileSidebar,
  onLogout,
}) => {
  const navItems: ConsoleModule[] = [ ...contentKinds, ...(user.is_superuser || user.permissions.can_manage_analytics ? ['analytics' as const] : []), 'account'];
  const permissionTags = [
    { label: '新闻', enabled: Boolean(user.is_superuser || user.permissions.can_manage_news) },
    { label: '资源', enabled: Boolean(user.is_superuser || user.permissions.can_manage_resources) },
    { label: '招聘', enabled: Boolean(user.is_superuser || user.permissions.can_manage_careers) },
    { label: '合作', enabled: Boolean(user.is_superuser || user.permissions.can_manage_project_cooperation) },
    { label: '统计', enabled: Boolean(user.is_superuser || user.permissions.can_manage_analytics) },
    { label: '用户', enabled: Boolean(user.is_superuser || user.permissions.can_manage_users) },
  ];

  return (
    <>
      <button
        type="button"
        className={`content-console-mobile-toggle${mobileSidebarOpen ? ' is-active' : ''}`}
        onClick={onToggleMobileSidebar}
        aria-expanded={mobileSidebarOpen}
        aria-controls="content-console-sidebar"
      >
        {'导航'}
      </button>

      <aside
        id="content-console-sidebar"
        className={[
          'content-console-app-sidebar',
          sidebarCollapsed ? 'is-collapsed' : '',
          mobileSidebarOpen ? 'is-mobile-open' : '',
        ].filter(Boolean).join(' ')}
      >
        <div className="content-console-sidebar-topbar">
          <div className="content-console-sidebar-brand">
            <img src={BrandLogo} alt="Future World" className="content-console-brand-logo" />
            <div className="content-console-sidebar-brand-copy">
              <h1>{'Future World 管理后台'}</h1>
            </div>
          </div>

          <div className="content-console-sidebar-topbar-actions">
            <button
              type="button"
              className="content-console-sidebar-icon-button is-desktop-only"
              onClick={onToggleCollapse}
              aria-label={sidebarCollapsed ? '展开导航' : '收起导航'}
              title={sidebarCollapsed ? '展开导航' : '收起导航'}
            >
              {sidebarCollapsed ? <ChevronRightIcon /> : <ChevronLeftIcon />}
            </button>
            <button type="button" className="content-console-sidebar-icon-button is-mobile-only" onClick={onCloseMobileSidebar} aria-label="关闭导航">
              x
            </button>
          </div>
        </div>

        <section className="content-console-sidebar-section">
          <span className="content-console-sidebar-label">{'工作区'}</span>
          <nav className="content-console-sidebar-nav">
            {navItems.map((item) => (
              <button
                key={item}
                type="button"
                className={`content-console-sidebar-nav-item${module === item ? ' is-active' : ''}`}
                onClick={() => {
                  onModuleChange(item);
                  onCloseMobileSidebar();
                }}
                title={moduleMeta[item].title}
              >
                <span className="content-console-sidebar-nav-rail">{moduleMeta[item].icon}</span>
                <span className="content-console-sidebar-nav-copy">
                  <strong>{moduleMeta[item].title}</strong>
                  <span>{moduleMeta[item].description}</span>
                </span>
              </button>
            ))}
          </nav>
        </section>

        <section className="content-console-sidebar-section">
          <span className="content-console-sidebar-label">{'当前账号'}</span>
          <div className="content-console-sidebar-user-card">
            <div>
              <strong>{user.display_name}</strong>
              <span>{user.username}</span>
            </div>
            <span className={`content-console-sidebar-role${user.is_superuser ? ' is-super' : ''}`}>
              {user.is_superuser ? '超级管理员' : '普通管理员'}
            </span>
          </div>
        </section>

        <section className="content-console-sidebar-section">
          <span className="content-console-sidebar-label">{'权限概览'}</span>
          <div className="content-console-sidebar-permissions">
            {permissionTags.map((item) => (
              <span key={item.label} className={`content-console-mini-tag${item.enabled ? ' is-enabled' : ''}`}>
                {item.label}
              </span>
            ))}
          </div>
        </section>

        <div className="content-console-sidebar-footer">
          <button type="button" className="content-console-secondary-button is-block" onClick={onLogout}>
            {'退出登录'}
          </button>
        </div>
      </aside>
    </>
  );
};

export default ConsoleTopbar;
